// @greenlight-spec: 11-observability.md#zombie-app-detection
// @greenlight-spec: 08-app-deployment.md
// @greenlight-spec: 15-database-schema.md#app_lifecycle
import { z } from 'zod';
/**
 * How long an app has gone unused, as its own dimension.
 *
 * Deliberately not folded into `AppStatus`: that status says what the workload
 * is doing (`deploying`, `active`, `degraded`, `failed`), and an idle app keeps
 * whatever operational status it had. Merging the two would make "degraded but
 * unused" unrepresentable and would put `inactive` inside the sets that gate
 * pause and deploy. `auto_suspended` is the one state that also moves
 * `apps.status` — through the kill switch, to `suspended`.
 *
 * Three states, one warning: an app goes quiet, its owner is told once that it
 * is scheduled to be paused, and then it is paused. There is no intermediate
 * "flagged" state — a second warning nobody can act on differently from the
 * first is just a second email.
 */
export const APP_ACTIVITY_STATES = ['active', 'inactive', 'auto_suspended'];
/**
 * Org-configurable day counts the daily scan classifies against. Each is
 * days-since-last-activity at which the app enters that state: `inactive_days`
 * is when the owner is warned, `auto_suspend_days` is when the app is paused.
 *
 * These only apply when the feature is on at all — see `enabled` on
 * {@link AppLifecycleSettingsSchema}. Ordering is enforced on write.
 */
export const AppLifecycleThresholdsSchema = z
    .object({
    inactive_days: z.number().int().min(1).max(3650),
    auto_suspend_days: z.number().int().min(1).max(3650),
})
    .refine((t) => t.auto_suspend_days > t.inactive_days, {
    message: 'auto_suspend_days must be greater than inactive_days',
    path: ['auto_suspend_days'],
});
/**
 * The org's whole configuration for the unused-app lifecycle: one switch, and
 * the day counts it runs on.
 *
 * `enabled: false` is the complete opt-out — nothing is classified, nobody is
 * emailed, and no app is ever paused for going unused. It is the default: an
 * install that has not made a decision about this does not start pausing
 * people's apps on day 90.
 */
export const AppLifecycleSettingsSchema = z.object({
    enabled: z.boolean(),
    thresholds: AppLifecycleThresholdsSchema,
});
/** The documented 30 / 90 day counts (docs/11 §Zombie App Detection). */
export const DEFAULT_APP_LIFECYCLE_THRESHOLDS = {
    inactive_days: 30,
    auto_suspend_days: 90,
};
/** Off, on the documented day counts, until an org turns it on. */
export const DEFAULT_APP_LIFECYCLE_SETTINGS = {
    enabled: false,
    thresholds: DEFAULT_APP_LIFECYCLE_THRESHOLDS,
};
/**
 * Reads the org's stored settings, falling back to the documented defaults.
 *
 * Falls back whole rather than per-field: a partially-parsed set could pair a
 * customized `inactive_days` with a default `auto_suspend_days`, and — worse —
 * could read as `enabled` from a shape this code does not understand. Anything
 * unparseable means the feature is off.
 */
export function parseAppLifecycleSettings(value) {
    const parsed = AppLifecycleSettingsSchema.safeParse(value);
    return parsed.success ? parsed.data : DEFAULT_APP_LIFECYCLE_SETTINGS;
}
/**
 * One app's stored inactivity state, as the API returns it. Derived figures
 * (days since activity, days until the pause) belong to the surfaces that read
 * them, not here — they are a function of when you ask.
 */
export const AppLifecycleStateSchema = z.object({
    app_id: z.string().uuid(),
    activity_state: z.enum(APP_ACTIVITY_STATES),
    state_entered_at: z.string(),
    activity_reset_at: z.string().nullable(),
    exempt: z.boolean(),
    exempt_reason: z.string().nullable(),
    delete_review_flagged_at: z.string().nullable(),
});
export const SetAppLifecycleExemptionSchema = z.object({
    exempt: z.boolean(),
    /** Why this app is idle by design. Required to exempt; ignored when clearing. */
    reason: z.string().min(1).max(500).nullish(),
});
/**
 * Settings patch. Partial because the settings surface sends only what changed;
 * the merged result is re-validated, so an edit that would leave the days out of
 * order is refused rather than stored.
 */
export const UpdateAppLifecycleSettingsSchema = z
    .object({
    enabled: z.boolean(),
    inactive_days: z.number().int().min(1).max(3650),
    auto_suspend_days: z.number().int().min(1).max(3650),
})
    .partial();
/**
 * What the app-detail banner needs to tell an owner their app is scheduled to be
 * paused, and nothing more. Returned on the app read; `activity_state` is
 * `active` (or the feature is off) for the overwhelming majority of apps, which
 * is the case the banner must stay silent for.
 */
export const AppInactivityNoticeSchema = z.object({
    activity_state: z.enum(APP_ACTIVITY_STATES),
    /** Whole days since the newest activity signal; null when there is no signal. */
    days_since_activity: z.number().int().nullable(),
    /** Days left before the app is paused; null when it is not on track to be paused. */
    days_until_pause: z.number().int().nullable(),
});
//# sourceMappingURL=app-lifecycle.js.map